import React, { useState, useEffect } from 'react';
import {
    Container,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Text,
    Box,
    Button,
    Flex,
    Spinner,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    HStack,
    Link as ChakraLink,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import PageTitle from './PageTitle';
import { useScoresByLeague } from '../hooks/useGameScores';
import { getTeamInfoById } from '../utils/getTeamInfo';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useUserContext } from '../contexts/UserContext';
import GlassCard from './GlassCard';
import dayjs from 'dayjs';
import TeamLogo from './TeamLogo';
import BroadcastGameModal, { type BroadcastGameRow } from './BroadcastGameModal';

const LeagueScoreboardCard: React.FC = () => {
    const { myTeam } = useUserContext();
    const myTeamId = myTeam ? Number(myTeam) : null;
    const leagueId = useMyLeagueStore((state) => state.myLeague?.id);
    const navigate = useNavigate();
    const myLeague = useMyLeagueStore((state) => state.myLeague);
    const league = myLeague as any;
    const { data: scoresData, isLoading: scoresLoading, error: scoresError } = useScoresByLeague(leagueId, 1, 20);

    const processedScores = React.useMemo(() => {
        if (!scoresData?.rows?.length) return [];
        const latestDate = scoresData.rows[0].scheduled_date;
        let latestGames = scoresData.rows.filter((r: Record<string, unknown>) => r.scheduled_date === latestDate);
        if (myTeamId) {
            latestGames = [...latestGames].sort((a, b) => {
                const aAway = Number(a.away_team_id);
                const aHome = Number(a.home_team_id);
                const bAway = Number(b.away_team_id);
                const bHome = Number(b.home_team_id);
                const aMine = aAway === myTeamId || aHome === myTeamId;
                const bMine = bAway === myTeamId || bHome === myTeamId;
                if (aMine && !bMine) return -1;
                if (!aMine && bMine) return 1;
                return 0;
            });
        }
        return latestGames;
    }, [scoresData, myTeamId]);

    const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
    const [broadcastGameId, setBroadcastGameId] = useState<number | null>(null);
    const [broadcastGameRow, setBroadcastGameRow] = useState<BroadcastGameRow | null>(null);


    const openBroadcast = (row: Record<string, unknown>) => {
        setBroadcastGameId(Number(row.game_id));
        setBroadcastGameRow({
            game_id: Number(row.game_id),
            scheduled_date: String(row.scheduled_date ?? ''),
            away_team_id: Number(row.away_team_id),
            home_team_id: Number(row.home_team_id),
            away_runs: Number(row.away_runs) ?? 0,
            home_runs: Number(row.home_runs) ?? 0,
            innings: Number(row.innings) || 9,
        });
        setIsBroadcastModalOpen(true);
    };

    const closeBroadcast = () => {
        setIsBroadcastModalOpen(false);
        setBroadcastGameId(null);
        setBroadcastGameRow(null);
    };

    if (!leagueId) {
        return (
            <Container maxW="container.xl" py={8}>
                <PageTitle mb={4}>League Scoreboard</PageTitle>
                <Text color="gray.500">Missing league ID.</Text>
                <Button onClick={() => navigate('/leagues')} colorScheme="blue" mt={4}>
                    League Management
                </Button>
            </Container>
        );
    }
    if (!league) {
        return (
            <Container maxW="container.xl" py={8}>
                <PageTitle mb={4}>League Scoreboard</PageTitle>
                <Text color="gray.500" mb={4}>
                    Your league is not loaded. Open a league from League Details first.
                </Text>
            </Container>
        );
    }

    return (
        <GlassCard maxW="max-content">
          <>
          <Text color="white.500" m={4} align="left">Today's Games</Text>

            {scoresLoading ? (
                <Flex justify="center" py={8}>
                    <Spinner size="lg" />
                </Flex>
            ) : processedScores.length > 0 ? (
                (() => {
                    const hasAnyExtraInnings = processedScores.some((r: Record<string, unknown>) => (Number(r.innings) || 0) > 9);
                    return (
                        <Box bg="transparent" borderWidth="1px" borderRadius="md" overflow="hidden" maxW="max-content">
                            <Table
                                size="sm"
                                variant="simple"
                                sx={{
                                    'th, td': { px: 1.5, py: 3.5, lineHeight: 'tight', bg: 'transparent' },
                                    'tbody tr': { bg: 'transparent' },
                                    'tbody td': {
                                        borderBottomWidth: '1px',
                                        borderBottomStyle: 'solid',
                                        borderBottomColor: 'gray.500',
                                    },
                                }}
                            >
                                <Thead>
                                    {/* <Tr>
                                        <Th w="1%" whiteSpace="nowrap">Date</Th>
                                        <Th w="1%" maxW="180px"></Th>
                                        <Th w={6} px={0.5}></Th>
                                        <Th w="1%" maxW="180px"></Th>
                                       
                                        <Th w="1%" whiteSpace="nowrap" px={1}></Th>
                                    </Tr> */}
                                </Thead>
                                <Tbody>
                                    {processedScores.map((row: Record<string, unknown>) => {
                                        const gameId = row.game_id;
                                        const awayId = row.away_team_id as number;
                                        const homeId = row.home_team_id as number;
                                        const awayRuns = Number(row.away_runs) ?? 0;
                                        const homeRuns = Number(row.home_runs) ?? 0;
                                        const innings = Number(row.innings) || 9;
                                        const awayWon = awayRuns > homeRuns;
                                        const homeWon = homeRuns > awayRuns;
                                        const awayInfo = getTeamInfoById(awayId, league as Parameters<typeof getTeamInfoById>[1]);
                                        const homeInfo = getTeamInfoById(homeId, league as Parameters<typeof getTeamInfoById>[1]);
                                        const awayName = awayInfo?.name ?? `Team ${awayId}`;
                                        const homeName = homeInfo?.name ?? `Team ${homeId}`;
                                        return (
                                            <Tr key={String(gameId)}>
                                                <Td whiteSpace="nowrap" verticalAlign="middle">{row.scheduled_date ? dayjs(String(row.scheduled_date)).format('MMM D, YYYY') : '—'}</Td>
                                                <Td maxW="180px" verticalAlign="middle" overflow="hidden" px={1}>
                                                    <HStack spacing={1} minW={0}>
                                                        {awayInfo && <Box flexShrink={0}><TeamLogo team={{ name: awayInfo.name, logo: awayInfo.logo, color_primary: awayInfo.color_primary, color_secondary: awayInfo.color_secondary, color_text: awayInfo.color_text }} size="sm" />&nbsp;</Box>}
                                                        <Box minW={0} flex={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                                            <Text as="span" fontWeight={awayWon ? 'bold' : 'medium'} fontSize="sm" title={awayName}>{awayName}</Text>
                                                        </Box>
                                                        {/* <Text as="span" fontWeight={awayWon ? 'bold' : 'normal'} fontSize="sm" flexShrink={0}>{awayRuns}</Text> */}
                                                    </HStack>
                                                </Td>
                                                <Td color="gray.500" textAlign="center" verticalAlign="middle" fontSize="xs" px={0.5}>At</Td>
                                                <Td maxW="180px" verticalAlign="middle" overflow="hidden" px={1}>
                                                    <HStack spacing={1} minW={0}>
                                                        {homeInfo && <Box flexShrink={0}><TeamLogo team={{ name: homeInfo.name, logo: homeInfo.logo, color_primary: homeInfo.color_primary, color_secondary: homeInfo.color_secondary, color_text: homeInfo.color_text }} size="sm" />&nbsp;</Box>}
                                                        <Box minW={0} flex={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                                            <Text as="span" fontWeight={homeWon ? 'bold' : 'medium'} fontSize="sm" title={homeName}>{homeName}</Text>
                                                        </Box>
                                                        {/* <Text as="span" fontWeight={homeWon ? 'bold' : 'normal'} fontSize="sm" flexShrink={0}>{homeRuns}</Text> */}
                                                    </HStack>
                                                </Td>
                                                {/* {hasAnyExtraInnings && (
                                                    <Td verticalAlign="middle" w={8} whiteSpace="nowrap" fontSize="xs" color="gray.500" px={0.5}>
                                                        {innings > 9 ? `(${innings})` : ''}
                                                    </Td>
                                                )} */}
                                                <Td verticalAlign="middle" whiteSpace="nowrap" px={1}>
                                                    <HStack spacing={2} fontSize="xs">
                                                        <ChakraLink as="button" onClick={() => openBroadcast(row)} color="blue.300" _hover={{ textDecoration: 'underline', color: 'blue.200' }}>Live Broadcast</ChakraLink>
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    );
                })()
            ) : (
                <Text color="gray.500">No games found.</Text>
            )
        }

            {
                isBroadcastModalOpen && (
                    <BroadcastGameModal
                        isOpen={isBroadcastModalOpen}
                        onClose={closeBroadcast}
                        gameId={broadcastGameId}
                        gameRow={broadcastGameRow}
                        league={league}
                    />
                )
            }
            </>
        </GlassCard>
    )
}


export default LeagueScoreboardCard;
